import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Palette, Gift, Percent, Ticket, Package, Users, CalendarClock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Switch } from "@/components/ui/switch";
import { inputCls } from "@/components/hub/Common";
import { stickerUrl } from "@/components/editor/stickers/fx";
import { loadHoliday } from "@/components/holiday/useHoliday";

const Row = ({ icon: Icon, label, on, onToggle, testid, children }) => (
  <div className="flex flex-wrap items-center gap-3 py-2.5 border-t border-white/5">
    <Icon className="w-4 h-4 text-slate-400" />
    <span className="text-sm text-slate-200 flex-1 min-w-[150px]">{label}</span>
    {children}
    <Switch checked={on} onCheckedChange={onToggle} data-testid={testid} />
  </div>
);

const DAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const DATE_OK = { test: (v) => v === "easter" || v === "blackfriday" || (/^\d{2}-\d{2}$/.test(v) && (([m, d]) => m >= 1 && m <= 12 && d >= 1 && d <= DAYS[m - 1])(v.split("-").map(Number))) };

const Schedule = ({ th, c, s, patch }) => {
  const { t, lang } = useI18n();
  const [v, setV] = useState({ start: c.start, end: c.end });
  useEffect(() => setV({ start: c.start, end: c.end }), [c.start, c.end]);
  const fmt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { day: "numeric", month: "short" });
  const auto = (x) => x === "easter" || x === "blackfriday";
  const field = (k) => auto(v[k]) ? <span className="text-[11px] font-mono text-slate-300 px-2 py-1.5 rounded bg-white/5" data-testid={`hol-${th.id}-${k}`}>{t("hol.sch_auto")}</span> : (
    <input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} placeholder="MM-JJ" maxLength={5}
      onBlur={() => { if (v[k] !== c[k]) { if (DATE_OK.test(v[k])) patch({ [k]: v[k] }); else { setV({ ...v, [k]: c[k] }); toast.error(t("hol.sch_bad")); } } }}
      className={`${inputCls} !w-20 !py-1.5 text-center font-mono`} data-testid={`hol-${th.id}-${k}`} />
  );
  const isDefault = c.start === s.default[0] && c.end === s.default[1];
  return (
    <div className="py-2.5 border-t border-white/5" data-testid={`hol-${th.id}-schedule`}>
      <div className="flex flex-wrap items-center gap-3">
        <CalendarClock className="w-4 h-4 text-slate-400" />
        <span className="text-sm text-slate-200 flex-1 min-w-[150px]">{t("hol.sch_label")}</span>
        {c.auto_on && s.in_window && <span className="text-[10px] font-mono uppercase rounded-full px-2 py-0.5 bg-[#22e3a1]/15 text-[#22e3a1] border border-[#22e3a1]/40" data-testid={`hol-${th.id}-live`}>{t("hol.sch_live")}</span>}
        <Switch checked={c.auto_on} onCheckedChange={(x) => patch({ auto_on: x })} data-testid={`hol-${th.id}-auto`} />
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2 pl-7 text-[11px] text-slate-500">
        {field("start")}<span>→</span>{field("end")}
        <span data-testid={`hol-${th.id}-window`}>{t(c.auto_on && s.in_window ? "hol.sch_current" : "hol.sch_window", { a: fmt(s.from), b: fmt(s.to) })}</span>
        {!isDefault && <button type="button" onClick={() => patch({ start: s.default[0], end: s.default[1] })} className="text-[#ff9df0] hover:underline" data-testid={`hol-${th.id}-reset`}>{t("hol.sch_reset")}</button>}
      </div>
    </div>
  );
};

const ThemeCard = ({ th, c, s, skinOn, setSkin, patch, codes }) => {
  const { t, lang } = useI18n();
  const [d, setD] = useState(c);
  useEffect(() => setD(c), [c]);
  const num = (k, min, max) => (
    <input type="number" min={min} max={max} value={d[k]} onChange={(e) => setD({ ...d, [k]: Number(e.target.value) })} onBlur={() => { const v = Math.min(max, Math.max(min, d[k] || min)); if (v !== c[k]) patch({ [k]: v }); }}
      className={`${inputCls} !w-20 !py-1.5 text-center`} data-testid={`hol-${th.id}-${k}`} />
  );
  return (
    <div className="cx-card rounded-2xl p-5" style={{ boxShadow: skinOn ? `0 0 30px -10px ${th.color}` : undefined, borderColor: skinOn ? `${th.color}88` : undefined }} data-testid={`hol-card-${th.id}`}>
      <div className="flex items-center gap-3">
        <span className="w-3 h-3 rounded-full" style={{ background: th.color, boxShadow: `0 0 10px ${th.color}` }} />
        <span className="font-display text-lg font-bold text-white flex-1">{lang === "fr" ? th.fr : th.en}</span>
        <span className="text-[11px] text-slate-400 flex items-center gap-1" data-testid={`hol-${th.id}-owners`}><Users className="w-3.5 h-3.5" />{t("hol.adm_owners", { n: th.owners })}</span>
      </div>
      <div className="flex gap-1.5 my-3">{th.stickers.map((s) => <img key={s.id} src={stickerUrl(s.id)} alt={s.text} className="w-11 h-11 object-contain" />)}</div>
      <Row icon={Palette} label={t("hol.adm_skin")} on={skinOn} onToggle={(v) => setSkin(v ? th.id : null)} testid={`hol-${th.id}-skin`} />
      <Schedule th={th} c={c} s={s} patch={patch} />
      <Row icon={Package} label={t("hol.adm_pack")} on={c.pack_on} onToggle={(v) => patch({ pack_on: v })} testid={`hol-${th.id}-pack`}>
        <span className="text-[11px] text-slate-500">{t("hol.adm_price")}</span>{num("price", 1, 500)}
      </Row>
      <Row icon={Percent} label={t("hol.adm_bonus")} on={c.bonus_on} onToggle={(v) => patch({ bonus_on: v })} testid={`hol-${th.id}-bonus`}>
        {num("bonus_pct", 1, 200)}<span className="text-[11px] text-slate-500">%</span>
      </Row>
      <Row icon={Ticket} label={t("hol.adm_promo")} on={c.promo_on} onToggle={(v) => patch({ promo_on: v })} testid={`hol-${th.id}-promo`}>
        <input list="hol-promo-codes" value={d.promo_code} onChange={(e) => setD({ ...d, promo_code: e.target.value.toUpperCase() })} onBlur={() => { if (d.promo_code !== c.promo_code) patch({ promo_code: d.promo_code }); }}
          placeholder="NOEL2026" maxLength={24} className={`${inputCls} !w-36 !py-1.5 font-mono uppercase`} data-testid={`hol-${th.id}-promo_code`} />
      </Row>
      <Row icon={Gift} label={t("hol.adm_free")} on={c.free_pack_on} onToggle={(v) => patch({ free_pack_on: v })} testid={`hol-${th.id}-free`} />
      {c.promo_on && c.promo_code && !codes.includes(c.promo_code) && <p className="text-[11px] text-amber-300 mt-1" data-testid={`hol-${th.id}-promo-warn`}>{t("hol.adm_promo_missing")}</p>}
    </div>
  );
};

export const HolidaysTab = () => {
  const { t, lang } = useI18n();
  const [d, setD] = useState(null);
  const [codes, setCodes] = useState([]);
  useEffect(() => {
    api.get("/admin/holidays").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/promos").then((r) => setCodes((r.data || []).map((p) => p.code))).catch(() => {});
  }, []);
  const ref = useRef(null);
  const queue = useRef(Promise.resolve());
  if (!d) return null;
  if (!ref.current) ref.current = d;
  const save = (next) => {
    ref.current = { ...ref.current, ...next };
    setD(ref.current);
    const body = { skin: next.skin, themes: next.themes };
    queue.current = queue.current.then(() => api.put("/admin/holidays", body))
      .then(() => { loadHoliday(true); toast.success(t("hol.adm_saved")); return api.get("/admin/holidays"); })
      .then((r) => { if (r) setD((x) => ({ ...x, schedule: r.data.schedule, live_skin: r.data.live_skin })); })
      .catch((e) => { toast.error(formatApiError(e)); api.get("/admin/holidays").then((r) => { ref.current = r.data; setD(r.data); }); });
  };
  const patch = (k) => (p) => { const cur = ref.current; save({ skin: cur.skin, themes: { ...cur.themes, [k]: { ...cur.themes[k], ...p } } }); };
  return (
    <div data-testid="holidays-tab">
      <p className="text-sm text-slate-400 mb-2 max-w-3xl">{t("hol.adm_intro")}</p>
      <p className="text-xs text-slate-500 mb-5 max-w-3xl" data-testid="hol-live-skin">{t("hol.sch_intro")} {d.live_skin ? t("hol.sch_now", { n: d.catalog.find((x) => x.id === d.live_skin)?.[lang === "fr" ? "fr" : "en"] }) : t("hol.sch_none")}</p>
      <datalist id="hol-promo-codes">{codes.map((c) => <option key={c} value={c} />)}</datalist>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {d.catalog.map((th) => <ThemeCard key={th.id} th={th} c={d.themes[th.id]} s={d.schedule[th.id]} skinOn={d.skin === th.id} codes={codes}
          setSkin={(skin) => save({ skin, themes: ref.current.themes })} patch={patch(th.id)} />)}
      </div>
    </div>
  );
};
